import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import { Mail } from "lucide-react";
import debounce from "lodash/debounce";
import {
  deleteEmailTemplate,
  getEmailTemplates,
  getMailerMetas,
} from "../../../services/MailerService";
import { useAuth } from "../../../context/AuthContext";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../common/useScrollLoadMore";
import NewCustomAsyncDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../Trainer/TrainerDashboard.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";
import { confirm } from '../common/Confirm/confirm';

const DASH = "—";
const MAILER_PAGE_SIZE = 20;

const textOf = (value) => {
  const s = String(value ?? "").trim();
  return s === "" ? DASH : s;
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/* The mailer a template sends through. Only ever its name -- the field
   can also come back as a bare id, and an id is not an answer. */
const mailerOf = (row) => {
  const list = row?.mailermetaIds;
  if (Array.isArray(list) && list.length > 0) return list[0] || null;
  return null;
};

const mailerNameOf = (row) => {
  const mailer = mailerOf(row);
  return mailer?.name ? String(mailer.name).trim() : "";
};

const attachmentCount = (row) => {
  const a = row?.attachments;
  if (!a) return 0;
  if (Array.isArray(a)) return a.length;
  if (typeof a === "object") return Object.keys(a).length;
  return 0;
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const TemplateDetailModal = ({ detail, onClose, onEdit, onDelete, canEdit, canDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const isActive = !detail.isDeleted;
  const statusLabel = isActive ? "Active" : "Inactive";
  const mailer = mailerOf(detail);
  const mailerName = mailerNameOf(detail);
  const attachments = attachmentCount(detail);

  const template = [
    { label: "Name", value: textOf(detail.name) },
    { label: "Slug", value: textOf(detail.slug), mono: true },
    { label: "Subject", value: textOf(detail.subject) },
    { label: "Title", value: textOf(detail.title) },
    { label: "Created", value: dateOf(detail.createdAt) },
    { label: "Last updated", value: dateOf(detail.updatedAt) },
  ];

  /* The mailer's own settings, and only where the join brought them. */
  const mailerFacts = mailer
    ? [
        { label: "Name", value: textOf(mailer.name) },
        { label: "Provider", value: textOf(mailer.provider) },
        { label: "Host", value: textOf(mailer.host) },
        { label: "Port", value: textOf(mailer.port) },
        { label: "From email", value: textOf(mailer.fromEmail) },
      ]
    : [];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="et-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The template is what the record is about, so its name is the
            title -- in full, never clipped the way the list clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="et-detail-title">
              {detail.name || "Email template"}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Slug</dt>
                <dd>{textOf(detail.slug)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Mailer</dt>
                <dd>{mailerName || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Template</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td className={f.mono ? "pgm-mono" : undefined}>{f.value}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="pgm-num">{template.length + 1}</td>
                      <td>Status</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            isActive ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Sends through</h3>
                {mailerFacts.length === 0 ? (
                  <p className="pgm-empty">
                    No mailer configuration came back with this template.
                  </p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mailerFacts.map((f, i) => (
                        <tr key={f.label}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{f.label}</td>
                          <td>{f.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Attachments
                  <span className="pgm-section-count">{attachments}</span>
                </h3>
                {attachments === 0 ? (
                  <p className="pgm-empty">This template carries no attachments.</p>
                ) : (
                  <p className="pgm-sub">
                    {attachments} attachment{attachments === 1 ? "" : "s"} are
                    configured. They are edited with the template.
                  </p>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Description</h3>
                {detail.description ? (
                  <p className="pgm-sub">{detail.description}</p>
                ) : (
                  <p className="pgm-empty">This template carries no description.</p>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">HTML</h3>
                {detail.html ? (
                  /* The source, shown as text. The old popup injected this
                     into the page, so the "preview" rendered whatever the
                     record happened to hold -- and lost the monospace it
                     claimed to be in. Nothing is truncated any more either;
                     the pane scrolls. */
                  <pre className="pgm-code">{detail.html}</pre>
                ) : (
                  <p className="pgm-empty">This template has no HTML body.</p>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {canEdit && (
                <button type="button" className="pgm-btn" onClick={onEdit}>
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const ListEmailTemplate = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 25 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("name");
  const [mailerDraft, setMailerDraft] = useState(null);

  // What is actually in force.
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [mailerId, setMailerId] = useState("");

  // The mailer picker's own paged option list.
  const [mailerOptions, setMailerOptions] = useState([]);
  const [mailerPage, setMailerPage] = useState(1);
  const [mailerHasMore, setMailerHasMore] = useState(true);
  const [mailerLoading, setMailerLoading] = useState(false);
  const [mailerSearch, setMailerSearch] = useState("");

  const isFetching = useRef(false);
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  const hasShownToast = useRef(false);

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canView = hasPermission("list-email-template", "view", "lms");
  const canAdd = hasPermission("list-email-template", "add", "lms");
  const canEdit = hasPermission("list-email-template", "update", "lms");
  const canDelete = hasPermission("list-email-template", "delete", "lms");

  useEffect(() => {
    if (!canView) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view Email Templates.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [canView, navigate]);

  // ─── The mailer picker ────────────────────────────────────────────────

  const loadMailers = useCallback(async (search, page, append) => {
    setMailerLoading(true);
    try {
      const response = await getMailerMetas(MAILER_PAGE_SIZE, page, search || "");
      const list = Array.isArray(response?.data) ? response.data : [];
      const options = list.map((m) => ({ value: m.id, label: m.name, mailer: m }));
      setMailerOptions((prev) => (append ? [...prev, ...options] : options));
      setMailerHasMore(list.length === MAILER_PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching mailer metas:", error);
      if (!append) setMailerOptions([]);
      setMailerHasMore(false);
    } finally {
      setMailerLoading(false);
    }
  }, []);

  const handleMailerSearch = useMemo(
    () =>
      debounce((value) => {
        setMailerSearch(value);
        setMailerPage(1);
        loadMailers(value, 1, false);
      }, 500),
    [loadMailers],
  );

  // ─── Fetch ────────────────────────────────────────────────────────────

  const fetchEmailTemplates = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    // Captured before the request: the overlay must never appear for a
    // scroll batch, and reading the live ref after an await could get the
    // wrong answer if anything reset it meanwhile.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      /* The endpoint takes one search pair, so a mailer filter is sent as
         that pair. Picking a mailer therefore replaces the typed search
         rather than narrowing it -- which is what the old page did too,
         only without saying so. */
      let value = searchTerm.trim();
      let field = value && searchField !== "all" ? searchField : "";
      if (mailerId) {
        value = mailerId;
        field = "mailermetaIds";
      }
      if (!value) field = "";

      const response = await getEmailTemplates(
        recordsPerPage,
        currentPage,
        value,
        field,
      );

      const records = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setTotalRecords(
        Number(
          response?.pagination?.total ?? (isAppend ? totalRecords : records.length),
        ),
      );

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...records.filter((r) => !seen.has(r.id))];
        });
      } else {
        setRows(records);
      }
      appendRef.current = false;
    } catch (error) {
      console.error("Error fetching email templates:", error);
      toast.error("Error loading email templates!");
      if (!isAppend) {
        setRows([]);
        setTotalRecords(0);
      }
      appendRef.current = false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
    // totalRecords is only read to hold the previous figure on an append;
    // depending on it would refetch every time it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsPerPage, currentPage, searchTerm, searchField, mailerId]);

  useEffect(() => {
    if (canView) fetchEmailTemplates();
  }, [fetchEmailTemplates, canView]);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (rows.length >= totalRecords) return;
    const nextPage = Math.floor(rows.length / recordsPerPage) + 1;
    // Setting the page to what it already is changes no state and runs no
    // effect, which would strand appendRef at true and make the next fresh
    // load append to the list it was supposed to replace.
    if (nextPage === currentPage) return;
    appendRef.current = true;
    setCurrentPage(nextPage);
  }, [loading, loadingMore, rows.length, totalRecords, recordsPerPage, currentPage]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, rows.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term, field) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setSearchField(field);
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchTerm(searchDraft.trim());
    setSearchField(searchFieldDraft);
    setMailerId(mailerDraft ? mailerDraft.value : "");
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setSearchFieldDraft("name");
    setMailerDraft(null);
    setSearchTerm("");
    setSearchField("name");
    setMailerId("");
    setCurrentPage(1);
    setMailerSearch("");
    setMailerPage(1);
    setMailerOptions([]);
    setMailerHasMore(true);
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    Boolean(searchTerm) || searchField !== "name" || Boolean(mailerId);

  const handleDelete = async (id) => {
    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this Email Template?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteEmailTemplate(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      // Rebuild the list from page 1 so the row is gone and the count is
      // right, rather than leaving a hole in a scrolled list.
      appendRef.current = false;
      if (currentPage === 1) fetchEmailTemplates();
      else setCurrentPage(1);
      toast.success("The email template has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting email template:", error);
      toast.error("Failed to delete the email template. Please try again.");
    }
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. S.No, the description, the HTML body, the
     attachments, both timestamps and the row of action buttons are all in
     the popup a row opens -- the same trade the attendance list makes. */
  const headers = [
    {
      title: "Name",
      prop: "name",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.name ? (
          <span className="pg-strong">{row.name}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Slug",
      prop: "slug",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.slug ? (
          <span className="pgm-mono">{row.slug}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Subject",
      prop: "subject",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.subject ? (
          <span className="pg-course" title={row.subject}>
            {row.subject}
          </span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Mailer",
      prop: "mailermetaIds",
      filterable: true,
      cell: (row) => {
        const name = mailerNameOf(row);
        return name ? <span>{name}</span> : <span className="pg-none">{DASH}</span>;
      },
    },
    {
      title: "Status",
      prop: "isDeleted",
      filterable: true,
      sortable: true,
      cell: (row) => (
        <span className={`pg-pill ${row.isDeleted ? "pg-pill--bad" : "pg-pill--good"}`}>
          {row.isDeleted ? "Inactive" : "Active"}
        </span>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      <TemplateDetailModal
        detail={detail}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          setDetail(null);
          if (id) navigate(`/add-email-template/${id}`);
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so picking two filters is one refetch. */}
      <FilterSheet label="Filter email templates">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="et-search">
              Search
            </label>
            <input
              id="et-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by name, slug or subject..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="et-search-field">
              Search in
            </label>
            <select
              id="et-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="name">Name</option>
              <option value="slug">Slug</option>
              <option value="subject">Subject</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="et-rows">
              Rows per load
            </label>
            <select
              id="et-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the picker, then the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label">Mailer</label>
              <NewCustomAsyncDropdown
                options={mailerOptions}
                value={mailerDraft ? [mailerDraft] : []}
                onChange={(selected) =>
                  setMailerDraft(
                    Array.isArray(selected) && selected.length > 0 ? selected[0] : null,
                  )
                }
                placeholder="All mailers"
                isMulti={false}
                isSearchable={true}
                isLoading={mailerLoading}
                searchInControl={true}
                onMenuOpen={() => {
                  if (mailerOptions.length === 0 && !mailerLoading) {
                    setMailerPage(1);
                    setMailerSearch("");
                    loadMailers("", 1, false);
                  }
                }}
                onInputChange={handleMailerSearch}
                onMenuScrollToBottom={() => {
                  if (mailerHasMore && !mailerLoading) {
                    const next = mailerPage + 1;
                    setMailerPage(next);
                    loadMailers(mailerSearch, next, true);
                  }
                }}
                onRefresh={() => {
                  setMailerSearch("");
                  setMailerPage(1);
                  loadMailers("", 1, false);
                }}
              />
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            {canAdd && (
              <>
                <span className="pg-filters-sep" aria-hidden="true" />
                <Link className="pg-btn pg-btn--main" to="/add-email-template">
                  Add template
                </Link>
              </>
            )}
          </div>
        </div>
      </FilterSheet>

      {/* --emailtpl: the rules that give a row its pointer and hover are
          unscoped, so the name has to be its own and unmistakable. */}
      <div className="td-list td-list--emailtpl" ref={listRef}>
        <NewCustomDataTable
          data={rows}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetail(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${row.name || "this template"}`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDetail(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {rows.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {rows.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} template{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Mail size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No email templates found</h3>
          <p className="pg-state-text">
            {hasActiveFilters
              ? "Nothing matches the filters in force."
              : "No email template has been created yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListEmailTemplate;
